import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import Loader from "components/Atoms/SharedComponents/Loader/Loader";
import { toast } from "react-toastify";
import dayjs from "dayjs";

import EventPlanningTemplate from "../../templates/event-planning";
import {
  getEventPlanningRequest,
  updateEventPlanningRequest,
  getEventPlanningDetailsRequest,
} from "../../services/modules/event-planning";
import { GetOccasionTypesRequest } from "../../services/modules/occasions/occasionsTypes";
import { getThemesRequest } from "../../services/modules/lookups/themes";
import { getServicesRequest } from "../../services/modules/lookups/services";

function EventPlanningPage() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  let [searchParams] = useSearchParams();
  const id = searchParams.get("id");
  const type = searchParams.get("type");

  const list = useSelector((state) => state.eventPlanning.eventPlanningList);
  const count = useSelector((state) => state.eventPlanning.count);
  const details = useSelector((state) => state.eventPlanning.eventPlanning);
  const load = useSelector((state) => state.eventPlanning.load);
  const occasions = useSelector((state) => state.occasionsTypes.occasionTypes);
  const themes = useSelector((state) => state.themes.themes);
  const services = useSelector((state) => state.services.services);

  // Permissions - assuming public or admin for now
  const can_create = true;

  const [page, setPage] = useState(1);

  const [filterObj, setFilterObj] = useState({
    page_size: 10,
    page_number: 1,
    // Add default filters if needed, e.g. status: 'pending'
  });

  const handlePageChange = (page) => {
    setPage(page);
    setFilterObj((prev) => {
      return { ...prev, page_number: page };
    });
  };

  const getDetails = useCallback(async () => {
    dispatch(getEventPlanningDetailsRequest({ id: id }));
  }, [dispatch, id]);

  const getList = useCallback(
    async (filterObj) => {
      dispatch(getEventPlanningRequest({ params: filterObj }));
    },
    [dispatch],
  );

  const getLookups = useCallback(async () => {
    dispatch(
      GetOccasionTypesRequest({
        params: { page_size: 50, page_number: 1 },
      }),
    );
    dispatch(
      getThemesRequest({
        params: { page_size: 50, page_number: 1 },
      }),
    );
    dispatch(
      getServicesRequest({
        params: { page_size: 50, page_number: 1 },
      }),
    );
  }, [dispatch]);

  // Map data for display
  const mappedData = Array.isArray(list)
    ? list.map((data) => {
        return {
          ...data,
          created_at: <>{dayjs(data?.created_at).format("DD-MM-YYYY HH:mm")}</>,
        };
      })
    : [];

  const breadcrumbs = [
    { active: true, path: "/", title: t("common.home") },
    {
      active: !type ? false : true,
      path: "/event-planning",
      title: t("event_planning.title"),
    },
    type && {
      active: false,
      title:
        type === "details"
          ? t("event_planning.details")
          : id
            ? t("event_planning.edit")
            : t("event_planning.add"),
    },
  ];

  const filterList = [
    {
      type: "filter",
      placeholder: t("event_planning.occasion_type"),
      onChange: (value) => {
        setFilterObj((s) => ({
          ...s,
          occasion_type_id: value?.value,
          page_number: 1,
        }));
      },
      options: occasions?.map((occasion) => ({
        id: occasion.id,
        name: occasion.localized_data?.en?.name || occasion.name,
      })),
    },
    {
      type: "filter",
      placeholder: t("event_planning.status"),
      onChange: (value) => {
        setFilterObj((s) => ({
          ...s,
          status: value?.value,
          page_number: 1,
        }));
      },
      options: [
        { id: "pending", name: t("event_planning.pending") },
        { id: "reviewed", name: t("event_planning.reviewed") },
        { id: "booked", name: t("event_planning.booked") },
      ],
    },
    {
      type: "date",
      placeholder: "",
      onChange: (value) => {
        if (value !== "01-01-1970") {
          setFilterObj((s) => ({
            ...s,
            event_date: value,
            page_number: 1,
          }));
        } else {
          setFilterObj((s) => ({
            ...s,
            event_date: "",
            page_number: 1,
          }));
        }
      },
      sx: {
        backgroundColor: "white",
      },
    },
  ];

  const handelSubmit = (data, formik) => {
    const action = () => {
      searchParams.delete("id");
      searchParams.delete("type");
      navigate("/event-planning");
      getList(filterObj);
      // reset form if needed
      toast.success(t("event_planning.updated"));
    };

    const requestData = {
      body: {
        location_type: data.location_type,
        environment: data.environment,
        occasion_type_id: data.occasion_id?.value,
        attendees_max: data.attendees_max,
        attendees_min: data.attendees_min,
        event_date: data.event_date,
        day_period: data.day_period,
        budget: data.budget,
        theme_id: data.theme_id?.value,
        service_ids: data.service_ids?.map((s) => s.value),
      },
      action,
    };

    if (id) {
      dispatch(updateEventPlanningRequest({ ...requestData, id }));
    }
    // Assuming create is not requested yet or separate flow
  };

  const actionsList = ({ index, data }) => {
    return [
      {
        click: () => {
          navigate(`?type=form&id=${data?.id}`);
        },
        text: t("common.edit"),
        condition: () => true, // Check perms
      },
    ];
  };

  const rowData = (data) => {
    return;
  };

  useEffect(() => {
    if (filterObj) getList(filterObj);
  }, [filterObj, getList]);

  useEffect(() => {
    if (id) getDetails();
  }, [id, getDetails]);

  useEffect(() => {
    getLookups();
  }, [getLookups]);

  return (
    <>
      <Loader open={load?.loading && load?.type === "list"} />
      <EventPlanningTemplate
        filterObj={filterObj}
        setFilterObj={setFilterObj}
        data={mappedData}
        count={count}
        paginationPage={page}
        handlePageChange={handlePageChange}
        details={details}
        breadcrumbs={breadcrumbs}
        filterList={filterList}
        handelSubmit={handelSubmit}
        actionsList={actionsList}
        can_create={can_create}
        occasions={occasions}
        themes={themes}
        services={services}
        rowData={rowData}
      />
    </>
  );
}

export default EventPlanningPage;
